import Add from "@mui/icons-material/Add";
import Check from "@mui/icons-material/Check";
import Close from "@mui/icons-material/Close";
import DeleteOutline from "@mui/icons-material/DeleteOutlined";
import EditOutlined from "@mui/icons-material/EditOutlined";
import Search from "@mui/icons-material/Search";
import { Box, IconButton, InputBase, Stack, Tooltip, Typography } from "@mui/material";
import axios, { type AxiosError } from "axios";
import { type KeyboardEvent,useEffect, useMemo, useRef, useState } from "react";

import apiRoutes from "../../constants/api-routes";
import useAuthHeader from "../../hooks/useAuthHeader";

// ─────────────────────────────────────────────────────────────────────────────
// TagPickerPanel — shared tag picker UI used by both:
//   • <TagManagement>  (Manage Tags button in the page header)
//   • <CoAppInlineTagsCell>  (per-row "+" tag adder)
//
// Layout (top to bottom):
//   1. Outlined-pill search input
//   2. Inline "Create new tag" row — collapses into an input on click
//   3. Scrollable list of existing tags. Each row:
//        • teal tag chip (label)
//        • spacer
//        • inline pencil → renames inline (no popup menu)
//        • inline trash  → confirms inline (no popup menu)
//      When `mode === "select"` (per-row inline cell), clicking a row's
//      label adds that tag to the resource and a check icon shows on the
//      row when it's already applied.
//
// All persistence (create / rename / delete) goes through /api/v1/tags via
// the existing apiRoutes — TagPickerPanel is fully self-contained for those
// flows, and the parent only needs to react to onTagsChanged() if it cares.
// ─────────────────────────────────────────────────────────────────────────────

export interface PickerTag {
	id: number | string;
	name: string;
}

interface TagPickerPanelProps {
	/**
	 * "manage" — pure tag library editor (header "Manage Tags").
	 * "select" — also lets the user click a tag row to add it to the active
	 *            resource. Highlights tags already applied via `appliedTagIds`.
	 */
	mode: "manage" | "select";
	/** Tag IDs already applied to the active row (only used in select mode). */
	appliedTagIds?: Set<string>;
	/** Called when the user clicks a tag in select mode. */
	onAddTag?: (tag: PickerTag) => void;
	/** Called whenever a tag is created / renamed / deleted so callers can refresh. */
	onTagsChanged?: () => void;
	/** Tight padding for the row-context inline picker; otherwise default chrome. */
	dense?: boolean;
}

const teal = "#02D5AB";
const tealText = "#066848";
const destructiveColor = "#B42318";

const inputBaseSx = {
	width: "100%",
	height: 36,
	background: "#FFFFFF",
	border: "1px solid rgba(13, 31, 48, 0.14)",
	borderRadius: 999,
	px: 1.5,
	fontSize: 13,
	color: "rgba(13, 31, 48, 0.86)",
	transition: "border-color 0.18s ease, box-shadow 0.18s ease",
	"&:hover": {
		borderColor: "rgba(2, 213, 171, 0.55)"
	},
	"&.Mui-focused": {
		borderColor: teal,
		boxShadow: `0 0 0 3px ${teal}2E`
	},
	"& input::placeholder": {
		color: "rgba(13, 31, 48, 0.45)",
		opacity: 1
	}
};

export default function TagPickerPanel({
	mode,
	appliedTagIds,
	onAddTag,
	onTagsChanged,
	dense = false
}: TagPickerPanelProps) {
	const authHeader = useAuthHeader();
	const [tags, setTags] = useState<PickerTag[]>([]);
	const [search, setSearch] = useState("");
	const [loading, setLoading] = useState(false);
	const [creating, setCreating] = useState(false);
	const [newTagName, setNewTagName] = useState("");
	const [createError, setCreateError] = useState("");
	const [editingTagId, setEditingTagId] = useState<string | null>(null);
	const [editingDraft, setEditingDraft] = useState("");
	const [editError, setEditError] = useState("");
	const [deletingTagId, setDeletingTagId] = useState<string | null>(null);
	const fetchedRef = useRef(false);

	const fetchTags = (created?: string) => {
		setLoading(true);
		axios
			.get<PickerTag[]>(apiRoutes.getTags, { headers: authHeader })
			.then((res) => {
				const fetched = res.data || [];
				if (created) {
					const lower = created.toLowerCase();
					const found = fetched.find((t) => t.name.toLowerCase() === lower);
					if (found) {
						setTags([found, ...fetched.filter((t) => String(t.id) !== String(found.id))]);
					} else {
						setTags(fetched);
					}
				} else {
					setTags(fetched);
				}
				fetchedRef.current = true;
			})
			.catch((err) => console.error("Failed to fetch tags", err))
			.finally(() => setLoading(false));
	};

	useEffect(() => {
		fetchTags();
		 
	}, []);

	const filteredTags = useMemo(() => {
		const q = search.trim().toLowerCase();
		if (!q) return tags;
		return tags.filter((t) => t.name.toLowerCase().includes(q));
	}, [tags, search]);

	const trimmedSearch = search.trim();
	const exactMatch = tags.some((t) => t.name.toLowerCase() === trimmedSearch.toLowerCase());

	const handleCreateTag = async (name?: string) => {
		const candidate = (name ?? newTagName).trim();
		if (!candidate) return;
		setCreateError("");
		try {
			await axios.post(apiRoutes.createTag, { name: candidate }, { headers: authHeader });
			setNewTagName("");
			setCreating(false);
			setSearch("");
			fetchTags(candidate);
			onTagsChanged?.();
		} catch (err) {
			const error = err as AxiosError<{ message?: string }>;
			const msg = error.response?.data?.message ?? error.message;
			setCreateError(msg ?? "Failed to create tag.");
		}
	};

	const handleStartEdit = (tag: PickerTag) => {
		setEditingTagId(String(tag.id));
		setEditingDraft(tag.name);
		setEditError("");
	};

	const handleCancelEdit = () => {
		setEditingTagId(null);
		setEditingDraft("");
		setEditError("");
	};

	const handleSaveEdit = async (tag: PickerTag) => {
		const candidate = editingDraft.trim();
		if (!candidate || candidate === tag.name) {
			handleCancelEdit();
			return;
		}
		setEditError("");
		try {
			await axios.put(`${apiRoutes.updateTag}/${tag.id}`, { name: candidate }, { headers: authHeader });
			setEditingTagId(null);
			setEditingDraft("");
			fetchTags();
			onTagsChanged?.();
		} catch (err) {
			const error = err as AxiosError<{ message?: string }>;
			const msg = error.response?.data?.message ?? error.message;
			setEditError(msg ?? "Failed to rename tag.");
		}
	};

	const handleDelete = async (tag: PickerTag) => {
		// One-click delete with an inline confirm pulse. The trash icon flips to
		// a "tap again to confirm" state; second click commits.
		const tagId = String(tag.id);
		if (deletingTagId !== tagId) {
			setDeletingTagId(tagId);
			window.setTimeout(() => {
				// Reset confirm state after 3s if the user doesn't tap again.
				setDeletingTagId((current) => (current === tagId ? null : current));
			}, 3000);
			return;
		}
		try {
			await axios.delete(`${apiRoutes.deleteTag}/${tag.id}`, { headers: authHeader });
			setDeletingTagId(null);
			fetchTags();
			onTagsChanged?.();
		} catch (err) {
			console.error("Failed to delete tag", err);
		}
	};

	const handleSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Enter" && trimmedSearch && !exactMatch) {
			e.preventDefault();
			handleCreateTag(trimmedSearch);
		}
	};

	return (
		<Box sx={{ width: dense ? 280 : 320, p: 1.5 }}>
			{/* Search */}
			<InputBase
				value={search}
				onChange={(e) => setSearch(e.target.value)}
				onKeyDown={handleSearchKeyDown}
				placeholder="Search tags…"
				autoFocus
				startAdornment={<Search sx={{ fontSize: 16, color: "rgba(13, 31, 48, 0.45)", mr: 1 }} />}
				sx={inputBaseSx}
			/>

			{/* Create new tag — inline row */}
			{trimmedSearch && !exactMatch ? (
				<Box
					onClick={() => handleCreateTag(trimmedSearch)}
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 1,
						mt: 1,
						px: 1.5,
						py: 0.875,
						borderRadius: 1.5,
						cursor: "pointer",
						background: "rgba(2, 213, 171, 0.06)",
						border: `1px dashed ${teal}55`,
						"&:hover": {
							background: "rgba(2, 213, 171, 0.10)",
							borderColor: teal
						}
					}}
				>
					<Add sx={{ fontSize: 16, color: tealText }} />
					<Typography sx={{ fontSize: 13, color: tealText, fontWeight: 600 }}>
						Create &quot;{trimmedSearch}&quot;
					</Typography>
				</Box>
			) : !creating ? (
				<Box
					onClick={() => setCreating(true)}
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 1,
						mt: 1,
						px: 1.5,
						py: 0.875,
						borderRadius: 1.5,
						cursor: "pointer",
						color: "rgba(13, 31, 48, 0.55)",
						"&:hover": {
							background: "rgba(2, 213, 171, 0.06)",
							color: tealText
						}
					}}
				>
					<Add sx={{ fontSize: 16 }} />
					<Typography sx={{ fontSize: 13, fontWeight: 500 }}>Create new tag</Typography>
				</Box>
			) : (
				<Stack direction="row" spacing={1} sx={{ mt: 1, alignItems: "center" }}>
					<InputBase
						value={newTagName}
						onChange={(e) => {
							setNewTagName(e.target.value);
							setCreateError("");
						}}
						onKeyDown={(e) => {
							if (e.key === "Enter") {
								e.preventDefault();
								handleCreateTag();
							} else if (e.key === "Escape") {
								setCreating(false);
								setNewTagName("");
								setCreateError("");
							}
						}}
						placeholder="New tag name"
						autoFocus
						sx={inputBaseSx}
					/>
					<IconButton
						size="small"
						onClick={() => handleCreateTag()}
						sx={{
							color: tealText,
							"&:hover": { background: "rgba(2, 213, 171, 0.10)" }
						}}
					>
						<Check sx={{ fontSize: 18 }} />
					</IconButton>
					<IconButton
						size="small"
						onClick={() => {
							setCreating(false);
							setNewTagName("");
							setCreateError("");
						}}
						sx={{ color: "rgba(13, 31, 48, 0.45)" }}
					>
						<Close sx={{ fontSize: 18 }} />
					</IconButton>
				</Stack>
			)}
			{createError ? (
				<Typography sx={{ mt: 0.5, ml: 1.5, fontSize: 11, color: destructiveColor }}>{createError}</Typography>
			) : null}

			{/* Tag list */}
			<Box sx={{ maxHeight: 300, overflowY: "auto", mt: 1, mx: -0.5 }}>
				{loading ? (
					<Typography sx={{ px: 2, py: 1.25, fontSize: 12, color: "rgba(13, 31, 48, 0.45)" }}>
						Loading tags…
					</Typography>
				) : filteredTags.length === 0 ? (
					<Typography sx={{ px: 2, py: 1.25, fontSize: 12, color: "rgba(13, 31, 48, 0.45)" }}>
						{trimmedSearch ? `No tags match "${trimmedSearch}"` : "No tags yet."}
					</Typography>
				) : (
					filteredTags.map((tag) => {
						const tagId = String(tag.id);
						const isEditing = editingTagId === tagId;
						const isApplied = appliedTagIds?.has(tagId) ?? false;
						const isConfirmingDelete = deletingTagId === tagId;
						const canSelect = mode === "select" && !isEditing && !isApplied;

						return (
							<Box
								key={tag.id}
								role={canSelect ? "button" : undefined}
								tabIndex={canSelect ? 0 : -1}
								onClick={() => {
									if (canSelect && onAddTag) onAddTag(tag);
								}}
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 1,
									px: 1.5,
									py: 0.625,
									borderRadius: 1.5,
									cursor: canSelect ? "pointer" : "default",
									"&:hover": canSelect
										? { background: "rgba(2, 213, 171, 0.06)" }
										: undefined,
									"&:hover .tag-row-actions": { opacity: 1 }
								}}
							>
								{isEditing ? (
									<Stack direction="row" spacing={0.75} sx={{ alignItems: "center", flex: 1 }}>
										<InputBase
											value={editingDraft}
											onChange={(e) => {
												setEditingDraft(e.target.value);
												setEditError("");
											}}
											onKeyDown={(e) => {
												if (e.key === "Enter") {
													e.preventDefault();
													handleSaveEdit(tag);
												} else if (e.key === "Escape") {
													handleCancelEdit();
												}
											}}
											autoFocus
											sx={{
												...inputBaseSx,
												height: 32,
												fontSize: 12
											}}
										/>
										<IconButton
											size="small"
											onClick={(e) => {
												e.stopPropagation();
												handleSaveEdit(tag);
											}}
											sx={{ color: tealText, "&:hover": { background: "rgba(2, 213, 171, 0.10)" } }}
										>
											<Check sx={{ fontSize: 16 }} />
										</IconButton>
										<IconButton
											size="small"
											onClick={(e) => {
												e.stopPropagation();
												handleCancelEdit();
											}}
											sx={{ color: "rgba(13, 31, 48, 0.45)" }}
										>
											<Close sx={{ fontSize: 16 }} />
										</IconButton>
									</Stack>
								) : (
									<>
										<Box
											component="span"
											sx={{
												display: "inline-flex",
												alignItems: "center",
												px: 1,
												py: 0.25,
												borderRadius: 999,
												background: "rgba(2, 213, 171, 0.12)",
												color: tealText,
												border: `1px solid ${teal}4D`,
												fontSize: 12,
												fontWeight: 600,
												letterSpacing: "0.02em",
												lineHeight: 1.4,
												whiteSpace: "nowrap",
												maxWidth: 180,
												overflow: "hidden",
												textOverflow: "ellipsis"
											}}
										>
											{tag.name}
										</Box>
										{mode === "select" && isApplied ? (
											<Typography sx={{ fontSize: 11, color: tealText, fontWeight: 600, ml: 0.5 }}>
												Applied
											</Typography>
										) : null}
										<Box sx={{ flex: 1 }} />
										<Stack
											direction="row"
											className="tag-row-actions"
											spacing={0.25}
											sx={{ opacity: isConfirmingDelete ? 1 : 0, transition: "opacity 0.18s ease", alignItems: "center" }}
											onClick={(e) => e.stopPropagation()}
										>
											{!isConfirmingDelete ? (
												<Tooltip title="Rename" arrow placement="top">
													<IconButton
														size="small"
														onClick={(e) => {
															e.stopPropagation();
															handleStartEdit(tag);
														}}
														sx={{
															width: 26,
															height: 26,
															color: "rgba(13, 31, 48, 0.45)",
															"&:hover": { background: "rgba(2, 213, 171, 0.10)", color: teal }
														}}
													>
														<EditOutlined sx={{ fontSize: 14 }} />
													</IconButton>
												</Tooltip>
											) : null}
											<Tooltip
												title={isConfirmingDelete ? "Tap again to confirm" : "Delete"}
												arrow
												placement="top"
											>
												<IconButton
													size="small"
													onClick={(e) => {
														e.stopPropagation();
														handleDelete(tag);
													}}
													sx={{
														width: 26,
														height: 26,
														color: isConfirmingDelete ? destructiveColor : "rgba(13, 31, 48, 0.45)",
														background: isConfirmingDelete ? "rgba(232, 80, 91, 0.08)" : "transparent",
														"&:hover": {
															background: "rgba(232, 80, 91, 0.10)",
															color: destructiveColor
														}
													}}
												>
													<DeleteOutline sx={{ fontSize: 14 }} />
												</IconButton>
											</Tooltip>
										</Stack>
									</>
								)}
							</Box>
						);
					})
				)}
				{editError ? (
					<Typography sx={{ mt: 0.25, mx: 1.5, fontSize: 11, color: destructiveColor }}>{editError}</Typography>
				) : null}
			</Box>
		</Box>
	);
}
